import type { ReactNode } from 'react'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { StatusChip, type ChipSize } from './Chip'
import { Pressable } from './Pressable'
import { useNls } from '../../nls/nlsProvider'

export interface DependencyLockReason {
  code: string
  title?: string
  targetId?: string
  inBin?: boolean
}

export interface DependencyLockChipProps {
  isLocked?: boolean
  reasons?: readonly DependencyLockReason[]
  label?: ReactNode
  ariaLabel?: string
  size?: ChipSize
  className?: string
  onClick?: () => void
  testId?: string
}

export const DependencyLockChip = ({
  isLocked = true,
  reasons = [],
  label,
  ariaLabel,
  size = 'sm',
  className,
  onClick,
  testId,
}: DependencyLockChipProps) => {
  const { t } = useNls()

  if (!isLocked) return null

  const effectiveLabel = label ?? t('NLSChecklistChipLocked')
  const defaultAria = ariaLabel ?? (reasons.length > 0
    ? `${t('NLSChecklistChipLocked')}: ${reasons.map((r) => (r.inBin ? t('NLSChecklistLockReasonInBin') : (r.title || r.code))).join(', ')}`
    : t('NLSChecklistChipLocked'))

  const content = (
    <StatusChip
      tone="danger"
      size={size}
      icon={<LockRoundedIcon fontSize="inherit" />}
      className={['dsDependencyLockChip', className ?? ''].filter(Boolean).join(' ')}
    >
      {effectiveLabel}
    </StatusChip>
  )

  if (onClick) {
    return (
      <Pressable
        onClick={onClick}
        aria-label={defaultAria}
        data-testid={testId}
        className="inline-flex"
      >
        {content}
      </Pressable>
    )
  }

  return (
    <span aria-label={defaultAria} data-testid={testId} className="inline-flex">
      {content}
    </span>
  )
}
